import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import type { DocumentProjectSummary } from '@avisoma/shared';
import { SortableTable } from './SortableTable';
import { Card } from './Card';
import { Link } from './Link';
import { formatDateTime } from '../lib/dateFormat';

export function DocumentProjects({ documentId }: { documentId: string }) {
  const query = useQuery({
    queryKey: ['documentProjects', documentId],
    queryFn: () => backend.getDocumentProjects(documentId),
  });

  if (query.isLoading) {
    return <div>Loading...</div>;
  }

  if (query.error) {
    return <div>Error: {query.error.message}</div>;
  }

  const projects = query.data?.data.projects ?? [];
  if (!projects.length) {
    return (
      <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Projects</h2>}>
        <p className="text-sm text-gray-500 dark:text-zinc-400">No linked projects</p>
      </Card>
    );
  }

  return <DocumentProjectsTable projects={projects} />;
}

function DocumentProjectsTable({ projects }: { projects: DocumentProjectSummary[] }) {
  return (
    <>
      <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Projects</h2>}>
        <SortableTable
          storageId="document-projects"
          rowKey={(row) => row.id}
          columns={[
            {
              accessor: 'id',
              label: 'ID',
              render: (row) => (
                <Link to={`/projects/${row.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  {row.id}
                </Link>
              ),
            },
            { accessor: 'esn', label: 'ESN', render: (row) => row.data?.projectData?.esn },
            { accessor: 'engineType', label: 'Engine Type', render: (row) => row.data?.projectData?.engineType },

            { accessor: 'engineTypeId', label: 'Engine Type ID' },
            {
              accessor: 'createdAt',
              label: 'Created',
              render: (row) => formatDateTime(row.createdAt),
            },
            {
              accessor: 'updatedAt',
              label: 'Updated At',
              render: (row) => formatDateTime(row.updatedAt),
            },
            { accessor: 'webhookUrl', label: 'Webhook URL' },
          ]}
          data={projects}
        />
      </Card>
    </>
  );
}
